/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import moment from "moment";
import SystemUser from "./messages/SystemUser";
import Recipient from "./messages/Recipient";
import { Loader } from "@/subframe/components/Loader";

interface Message {
  message_id?: string;
  id?: string;
  recipient?: string;
  system_user?: string;
  created_at: string;
  status?: "sent" | "delivered" | "read" | "passed";
  recipient_payload?: {
    entry: [
      {
        changes: [
          {
            value: {
              messages: [
                {
                  type:
                    | "text"
                    | "video"
                    | "document"
                    | "image"
                    | "audio"
                    | "button";
                  text?: {
                    body: string;
                  };
                  video?: {
                    caption?: string;
                    id?: number;
                  };
                  document?: {
                    caption?: string;
                    id?: number;
                    filename?: string;
                    mime_type?: string;
                  };
                  image?: {
                    caption?: string;
                    id?: number;
                  };
                  audio?: {
                    voice: boolean;
                  };
                }
              ];
            };
          }
        ];
      }
    ];
  };
  system_user_payload?: {
    image?: {
      id: number;
      caption: string;
    };
    document?: {
      id: number;
      caption: string;
      mime_type: any;
    };
    video?: {
      id: number;
      caption: string;
    };
    type: any;
    template: any;
  };
}

interface MessagesDataWithPagination {
  items: Message[];
  total?: number;
  page?: number;
  size?: number;
}

interface MessageProps {
  messages: MessagesDataWithPagination;
  scrollRef: React.RefObject<HTMLDivElement>;
  is24HoursWindowShow: boolean;
}

const Message: React.FC<MessageProps> = ({
  messages,
  scrollRef,
  is24HoursWindowShow,
}) => {
  // Group messages by date
  const groupedMessages = messages?.items?.reduce((acc, message) => {
    const messageDate = moment(message.created_at).format("YYYY-MM-DD");
    if (!acc[messageDate]) {
      acc[messageDate] = [];
    }
    acc[messageDate].push(message);
    return acc;
  }, {} as Record<string, typeof messages.items>);

  return (
    <div className="relative flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 bg-gradient-to-br from-slate-50 via-amber-50/3 to-orange-50/2 px-8 py-8 overflow-auto custom-scrollbar">
      {/* 24 Hours Window Alert */}
      {is24HoursWindowShow && (
        <div className="fixed top-24 left-1/2 w-full max-w-lg z-20 flex justify-center items-center -translate-x-1/2 md:left-2/3 md:-translate-x-1/2">
          <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-orange-200/50 p-4 mx-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">24-hour window closed</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  You can only send personalized messages within 24 hours of the last customer message.
                </p>
              </div>
              <button className="flex-shrink-0 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages Container */}
      <div className="flex w-full flex-col items-start gap-6">
        {groupedMessages ? (
          Object.entries(groupedMessages).map(([date, dayMessages]) => (
            <div key={date} className="w-full space-y-4">
              {/* Enhanced Date Separator */}
              <div className="flex items-center gap-4 my-6">
                <hr className="flex-grow border-t border-gray-200/60 h-px" />
                <div className="px-4 py-2 bg-white/70 backdrop-blur-sm rounded-full shadow-sm border border-gray-100">
                  <span className="text-sm font-medium text-gray-600">
                    {moment(date).isSame(moment(), "day")
                      ? "Today"
                      : moment(date).isSame(moment().subtract(1, 'day'), "day")
                      ? "Yesterday"
                      : moment(date).format("dddd, MMMM D")}
                  </span>
                </div>
                <hr className="flex-grow border-t border-gray-200/60 h-px" />
              </div>

              {/* Messages for this date */}
              <div className="space-y-3">
                {dayMessages.map((message) => (
                  <React.Fragment key={message.id}>
                    {message.system_user ? (
                      <SystemUser message={message} scrollRef={scrollRef} />
                    ) : (
                      <Recipient message={message} scrollRef={scrollRef} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center w-full py-16">
            <div className="flex flex-col items-center gap-4">
              <Loader size="large" />
              <p className="text-gray-500 text-sm">Loading messages...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Message;
